import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { deptLabel } from "../../lib/labels";
import { toast } from "sonner";
import {
  Loader2,
  RefreshCw,
  GitBranch,
  CheckCircle2,
  AlertTriangle,
  Ban,
  Save,
  Trash2,
} from "lucide-react";
import PageHeader from "../../components/system/PageHeader";
import Button from "../../components/system/Button";

const STATUS_META = {
  unmapped: {
    label: "Unmapped",
    badge: "bg-rose-100 text-rose-700 border-rose-200",
    tone: "bad",
  },
  mapped: {
    label: "Mapped",
    badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
    tone: "good",
  },
  ignored: {
    label: "Ignored",
    badge: "bg-ink/10 text-ink/60 border-ink/20",
    tone: "muted",
  },
  direct_match: {
    label: "Direct match",
    badge: "bg-sky-100 text-sky-700 border-sky-200",
    tone: "muted",
  },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.unmapped;
  return (
    <span
      className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] font-display font-bold tracking-wide uppercase border ${meta.badge}`}
      data-testid={`status-badge-${status}`}
    >
      {meta.label}
    </span>
  );
}

function Summary({ items }) {
  const counts = useMemo(() => {
    const c = { unmapped: 0, mapped: 0, ignored: 0, direct_match: 0 };
    for (const it of items) c[it.status] = (c[it.status] || 0) + 1;
    return c;
  }, [items]);
  const cells = [
    { key: "unmapped", label: "Needs mapping" },
    { key: "mapped", label: "Mapped" },
    { key: "ignored", label: "Ignored" },
    { key: "direct_match", label: "Direct match" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="mapping-summary">
      {cells.map(({ key, label }) => {
        const meta = STATUS_META[key];
        return (
          <div
            key={key}
            className="border border-border bg-white p-3 flex flex-col gap-0.5"
            data-testid={`summary-${key}`}
          >
            <div className="data-sku text-[10px]">{label}</div>
            <div
              className={`font-display font-black text-3xl leading-none ${
                meta.tone === "bad"
                  ? "text-rose-600"
                  : meta.tone === "good"
                  ? "text-emerald-600"
                  : "text-ink"
              }`}
            >
              {counts[key] || 0}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MappingRow({ row, depts, onSaved }) {
  const [target, setTarget] = useState(row.target_department_id || "");
  const [ignore, setIgnore] = useState(!!row.ignore);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Reset when the underlying row changes (e.g. refetch).
  useEffect(() => {
    setTarget(row.target_department_id || "");
    setIgnore(!!row.ignore);
  }, [row.staging_name_key, row.target_department_id, row.ignore]);

  const isDirect = row.status === "direct_match";

  const onSelect = (e) => {
    const v = e.target.value;
    if (v === "__ignore__") {
      setIgnore(true);
      setTarget("");
    } else {
      setIgnore(false);
      setTarget(v);
    }
  };

  const canSave = !isDirect && (ignore || !!target);
  const isDirty =
    !isDirect &&
    (ignore !== !!row.ignore || target !== (row.target_department_id || ""));

  const save = async () => {
    if (!canSave) {
      toast.error("Pick a target department or choose Ignore.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        staging_name: row.staging_name,
        ...(ignore ? { ignore: true } : { target_department_id: target }),
      };
      await api.put("/admin/department-mappings", body);
      toast.success(`Saved mapping for ${deptLabel(row.staging_name)}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!row.mapping_id) return;
    setRemoving(true);
    try {
      await api.delete(`/admin/department-mappings/${row.mapping_id}`);
      toast.success(`Cleared mapping for ${deptLabel(row.staging_name)}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-[1fr_auto_minmax(220px,1fr)_auto] items-start gap-3 md:gap-4 px-4 py-3 border-b border-border last:border-0 hover:bg-secondary/40"
      data-testid={`mapping-row-${row.staging_name_key}`}
    >
      {/* Left: staging dept + sample */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="font-display font-bold text-base tracking-tight text-ink truncate"
            data-testid={`staging-name-${row.staging_name_key}`}
          >
            {deptLabel(row.staging_name)}
          </div>
          <span className="data-sku text-[10px]">
            <span className="font-mono text-ink">{row.row_count}</span> row
            {row.row_count === 1 ? "" : "s"}
            {row.active_row_count !== row.row_count && (
              <>
                {" · "}
                <span className="font-mono text-ink">{row.active_row_count}</span> active
              </>
            )}
          </span>
        </div>
        {row.sample_products?.length > 0 && (
          <div className="mt-1 text-[11px] text-ink/50 truncate font-mono">
            e.g.{" "}
            {row.sample_products
              .map((p) => p.name || p.barcode || "?")
              .filter(Boolean)
              .slice(0, 3)
              .join(" · ")}
          </div>
        )}
      </div>

      {/* Middle: status badge */}
      <div className="flex md:justify-center md:pt-1">
        <StatusBadge status={row.status} />
      </div>

      {/* Right: target select (or info for direct match) */}
      <div className="min-w-0">
        {isDirect ? (
          <div className="flex items-center gap-1.5 text-xs text-ink/60 md:pt-1">
            <CheckCircle2 size={14} className="text-emerald-600" />
            Auto-routed to {deptLabel(row.staging_name)}
          </div>
        ) : (
          <select
            value={ignore ? "__ignore__" : target || ""}
            onChange={onSelect}
            disabled={saving || removing}
            className="w-full h-10 px-3 border border-border rounded-sm bg-white text-sm"
            data-testid={`mapping-select-${row.staging_name_key}`}
          >
            <option value="">— Pick ShelfLife department —</option>
            {depts.map((d) => (
              <option key={d.id} value={d.id}>
                {deptLabel(d.name)}
              </option>
            ))}
            <option value="__ignore__">Ignore (skip these rows)</option>
          </select>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 md:pt-0.5">
        {!isDirect && (
          <button
            onClick={save}
            disabled={saving || !isDirty || !canSave}
            className="btn-primary h-10 px-3"
            data-testid={`mapping-save-${row.staging_name_key}`}
            title={isDirty ? "Save mapping" : "No changes"}
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            <span className="hidden sm:inline">Save</span>
          </button>
        )}
        {row.mapping_id && (
          <button
            onClick={remove}
            disabled={saving || removing}
            className="btn-secondary h-10 px-3 text-rose-600 border-rose-200 hover:border-rose-400"
            data-testid={`mapping-clear-${row.staging_name_key}`}
            title="Clear mapping (revert to direct match / unmapped)"
          >
            {removing ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}

export default function DepartmentMappingPage() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setErr(null);
      try {
        const { data: payload } = await api.get(
          "/admin/catalog/staging/departments",
        );
        setData(payload);
      } catch (e) {
        setErr(formatApiErrorDetail(e));
        toast.error(formatApiErrorDetail(e));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [],
  );

  useEffect(() => {
    load();
  }, [load]);

  const stagingDepartments = data?.staging_departments;
  const items = useMemo(() => stagingDepartments || [], [stagingDepartments]);
  const depts = data?.departments || [];

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => it.status === filter);
  }, [items, filter]);

  return (
    <div className="max-w-5xl" data-testid="admin-department-mapping-page">
      <PageHeader
        eyebrow="Catalog management"
        title="Department Mapping"
        subtitle={
          <>
            Decide what happens to each <span className="font-mono">Products_Staging.department_id</span>{" "}
            value at import time. Direct matches auto-route; unmapped values block import until you map
            them to an existing ShelfLife department or mark them <span className="font-display font-semibold">Ignore</span>.
          </>
        }
        actions={
          <Button
            tier="secondary"
            onClick={() => load({ silent: true })}
            disabled={loading || refreshing}
            data-testid="refresh-mapping-button"
          >
            {refreshing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
            {refreshing ? "Refreshing…" : "Refresh from sheet"}
          </Button>
        }
      />

      {loading && (
        <div className="flex items-center gap-2 text-ink/60 py-12" data-testid="mapping-loading">
          <Loader2 className="animate-spin" size={18} /> Reading Products_Staging…
        </div>
      )}

      {!loading && err && (
        <div
          className="border border-rose-200 bg-rose-50/60 p-4 rounded-sm flex items-start gap-2 mt-4"
          data-testid="mapping-error"
        >
          <AlertTriangle size={18} className="text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-rose-700">{err}</div>
        </div>
      )}

      {!loading && !err && data && (
        <>
          <Summary items={items} />

          <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="mapping-filter-chips">
            {[
              { key: "all", label: `All (${items.length})` },
              { key: "unmapped", label: `Unmapped (${items.filter((i) => i.status === "unmapped").length})` },
              { key: "mapped", label: `Mapped (${items.filter((i) => i.status === "mapped").length})` },
              { key: "ignored", label: `Ignored (${items.filter((i) => i.status === "ignored").length})` },
              { key: "direct_match", label: `Direct (${items.filter((i) => i.status === "direct_match").length})` },
            ].map(({ key, label }) => {
              const active = filter === key;
              return (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`h-8 px-3 rounded-full text-xs font-display tracking-wide border transition-colors ${
                    active
                      ? "bg-ink text-white border-ink"
                      : "bg-white text-ink/70 border-border hover:border-ink/40"
                  }`}
                  data-testid={`filter-${key}`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {filtered.length === 0 ? (
            <div
              className="border border-border bg-white px-5 py-10 text-center text-sm text-ink/60 flex flex-col items-center gap-2"
              data-testid="mapping-empty"
            >
              <GitBranch size={22} className="text-ink/40" />
              {filter === "unmapped" ? (
                <>
                  <div className="font-display font-bold text-ink text-base">All staging departments resolved</div>
                  <div>Nothing needs mapping right now. New unmapped values will appear here.</div>
                </>
              ) : (
                <>No staging departments in this view.</>
              )}
            </div>
          ) : (
            <div className="border border-border bg-white" data-testid="mapping-table">
              {filtered.map((row) => (
                <MappingRow
                  key={row.staging_name_key}
                  row={row}
                  depts={depts}
                  onSaved={() => load({ silent: true })}
                />
              ))}
            </div>
          )}

          {data.unique_staging_departments > 0 && (
            <div className="data-sku mt-3 flex items-center gap-3 flex-wrap" data-testid="mapping-footer">
              <span>
                <span className="font-mono text-ink">{data.unique_staging_departments}</span> unique staging
                department{data.unique_staging_departments === 1 ? "" : "s"} ·{" "}
                <span className="font-mono text-ink">{data.total_staging_rows}</span> total staging rows
              </span>
              <span className="flex items-center gap-1 text-ink/50">
                <Ban size={11} /> Mapping changes apply to the next Catalog Import
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
